import { Key, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Form } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { TableRowSelection } from 'antd/lib/table/interface';
import { GetSensorsListByAssetId } from 'core/domain/sensors/repositories/getSensorsListByAssetId';
import { SensorEntityModel, SensorEditEntityModel } from 'core/domain/sensors/models/sensorEntityModels';
import { EditSensorById } from 'core/domain/sensors/repositories/editSensorById';
import { UpdateSensorTagList } from 'core/domain/sensors/repositories/updateSensorTagList';
import { UpdateSensorTagListDataModel } from 'core/domain/sensors/models';
import { SensorColumns } from 'components/organisms/SensorColumns';
import { UserRoleType } from 'models/users.model';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { useProfileUser } from 'hooks/useProfileUser';
import { useMessage } from 'hooks/useMessage';
import { EditSensorFormValues } from 'components/molecules/EditSensorModal/config';
import { EditSensorFormModel, EditSensorOptionsModel } from 'components/molecules/EditSensorModal';
import { getFilters, parseFiltersToBeSent } from 'utils/filter';
import { FilterConditionModel, TableFilterBasicModel, TableFilterBasicOptionModel } from 'utils/globalModels';
import { FilterOperatorsType } from 'constants/filters';
import {
  TABLE_PAGINATION_PAGE_DEFAULT,
  TABLE_PAGINATION_SIZE_ALL_RESULTS,
  TABLE_PAGINATION_SIZE_DEFAULT,
  TablePaginationSearchValue,
} from 'constants/table';
import { AssetTabType } from 'components/pages/AssetPage';
import { getAssetWithParamsPathById } from 'components/pages/App/routes/assets/config';
import { MultipleTagEditorOptionsModel } from 'components/organisms/MultipleTagEditorModal';
import { ColumnsMultipleTagEditorModal } from 'components/organisms/MultipleTagEditorModal/Columns';
import { mapTagsToLabels } from 'utils/sensorTags';
import { CustomSensorModel, MultipleItemListModel } from './models';
import {
  defaultSensor,
  getFilteredMultipleTagEditorSelectedRows,
  getParsedSensorsToBeUpdated,
  getTagListSingleSensorData,
  MAX_SIZE_RESPONSIVE_SCREEN,
  parseTags,
  setParsedSensorList,
} from './utils';
import { SensorType, SensorUsage } from 'constants/sensors';
import { TableFilterModel, TableFilterType } from 'components/organisms/TableFilter/models';
import { sensorTypeLiterals, sensorUsageLiterals } from 'utils/sensors';

const sensorIdKey: keyof CustomSensorModel = 'sensorId';
const sensorTypeKey: keyof CustomSensorModel = 'sensorType';
const sensorUsageKey: keyof CustomSensorModel = 'usage';

const defaultFilter: FilterConditionModel[] = [{ field: sensorIdKey, operator: FilterOperatorsType.NULL, value: '' }];

export const useSensorOptionList = (showAll: boolean) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { assetId, tab } = useParams<{ assetId: string; tab: AssetTabType }>();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const page = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.PAGE);
  const size = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.SIZE);
  const filters = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.FILTERS);
  const { data: userData } = useProfileUser();
  const { setMessageSuccess, setMessageError } = useMessage();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sensors, setSensors] = useState<CustomSensorModel[]>([]);
  const [selectedSensors, setSelectedSensors] = useState<CustomSensorModel[]>([]);
  const [currentSelectedSensor, setCurrentSelectedSensor] = useState<CustomSensorModel>();
  const [isSavingTagList, setIsSavingTagList] = useState<boolean>(false);
  const [temporalTagList, setTemporalTagList] = useState<string[]>([]);
  const [form] = Form.useForm<EditSensorFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditSensorModalVisible, setIsEditSensorModalVisible] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [currentSize, setCurrentSize] = useState<number>(Number(TABLE_PAGINATION_SIZE_DEFAULT));
  const [currentFilterValue, setCurrentFilterValue] = useState<string>('');
  const [totalPages, setTotalPages] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [customFilters, setCustomFilters] = useState<TableFilterModel[]>([]);
  const [multipleTagEditorModalVisible, setMultipleTagEditorModalVisible] = useState<boolean>(false);
  const [multipleTagList, setMultipleTagList] = useState<MultipleItemListModel[]>([]);
  const [multipleTagEditorRowsSelected, setMultipleTagEditorRowsSelected] = useState<Key[]>([]);
  const [updating, setUpdating] = useState<boolean>(false);

  const isUserSuperAdmin = !!userData?.roles.includes(UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN);

  const assignTagsButtonText: string = t('_CONSUMPTION_SENSORS_LIST_ASSIGN_TAG_BUTTON_TEXT');
  const selectedSensorNumberText: string = !!selectedSensors.length
    ? t('_CONSUMPTION_SENSORS_LIST_SELECTED_SENSOR_NUMBER_TEXT', { number: selectedSensors.length })
    : t('_CONSUMPTION_SENSORS_LIST_SELECTED_SENSOR_NUMBER_TEXT_EMPTY');
  const deselectAllSensorsButtonText: string = t('_CONSUMPTION_SENSORS_LIST_DESELECT_ALL_SENSORS_BUTTON_TEXT');

  const sensorColumnsLiterals = {
    actionsText: t('actions'),
    editNameText: t('EDIT_NAME'),
    editTagButtonText: t('_CONSUMPTION_SENSORS_LIST_TAG_LIST_EDIT_BUTTON'),
    editTagPlaceholder: t('_CONSUMPTION_SENSORS_LIST_EDIT_TAG_PLACEHOLDER'),
    nameText: t('name'),
    roomText: t('room'),
    saveTagButtonText: t('to_save'),
    cancelTagButtonText: t('cancel'),
    showInStatisticsText: t('SHOW_IN_STATISTICS'),
    statisticsText: t('_CONSUMPTION_SENSORS_LIST_COLUMNS_STATISTICS_TEXT'),
    tagText: t('_CONSUMPTION_SENSORS_LIST_COLUMNS_TAG_TEXT'),
    emptyTagListText: t('_CONSUMPTION_SENSORS_LIST_COLUMNS_TAG_TEXT_EMPTY'),
    typeText: t('type'),
    usageText: t('usage'),
    tooltipMessage: t('_CUSTOM_TOOLTIP_TEXT'),
  };

  const editSensorModalLiterals = {
    title: t('EDIT_NAME'),
    secondaryButtonText: t('to_cancel'),
    mainButtonText: t('save'),
    alertMessage: t('EDIT_SENSOR_MODAL.TITLE'),
    alertDescription: t('EDIT_SENSOR_MODAL.INFO'),
    nameLabel: t('NEW_NAME'),
    requiredText: t('field_required'),
  };

  const multipleTagEditorLiterals = {
    title: t('_MULTIPLE_TAG_EDITOR_MODAL_TITLE'),
    columnsTitle: t('name'),
    mainButtonText: t('assign'),
    secondaryButtonText: t('cancel'),
    selectedTagsText: !!multipleTagEditorRowsSelected.length
      ? t('_MULTIPLE_TAG_EDITOR_MODAL_TEXT', { tags: multipleTagEditorRowsSelected.length })
      : t('_MULTIPLE_TAG_EDITOR_MODAL_EMPTY_TEXT'),
  };

  const getSensorList = async ({
    assetId,
    showAll,
    page,
    size,
    search,
    filters,
  }: {
    assetId: string;
    page: number;
    size: number;
    search?: string;
    filters: string;
    showAll: boolean;
  }) => {
    setIsLoading(true);
    try {
      const parsedFilters = parseFiltersToBeSent(filters);
      const response = await GetSensorsListByAssetId({
        host,
        token,
        assetId,
        showAll,
        page,
        size,
        search,
        filters: parsedFilters,
      });
      if (!!response.data?.length) {
        const parsedData = setParsedSensorList({
          data: response.data,
          selectedSensors,
          currentPage: page,
        });
        setSensors(parsedData);
        !!response.meta && setTotalPages(response.meta.totalPages);
      }
      !response.data?.length && setSensors([]);
    } catch (error) {
      setMessageError({ description: t('_GET_ASSET_SENSOR_LIST_ERROR_MESSAGE') });
    } finally {
      setIsLoading(false);
    }
  };

  const getSensorById = (id: string): CustomSensorModel => {
    const selected = sensors.find((item) => item.id === id);

    return !!selected ? selected : defaultSensor;
  };

  const onSelectSensorAsset = (sensor: SensorEntityModel) => {
    const [parsedSensor] = setParsedSensorList({ data: [sensor], currentPage });
    setSelectedSensors([parsedSensor]);
    setCurrentSelectedSensor(parsedSensor);
  };

  const onEditSensor = async (id: string, sensor: SensorEditEntityModel) => {
    setIsLoading(true);
    try {
      await EditSensorById(id, sensor);
      await getSensorList({ assetId, showAll, page: currentPage, size: currentSize, filters: currentFilterValue });
      setMessageSuccess({ description: t('successfully_updated') });
    } catch (error) {
      setMessageError({ description: t('EDIT_ERROR') });
    } finally {
      setIsLoading(false);
    }
  };

  const onOpenTagEditor = (id: string) => {
    const parsedSensors = sensors.map((sensor) => {
      const isEditingTagList = sensor.id === id;
      const parsedAvailableTags = isEditingTagList ? mapTagsToLabels(sensor.availableTags) : [];
      isEditingTagList && setTemporalTagList(sensor.tags);
      return { ...sensor, isEditingTagList, parsedAvailableTags };
    });
    setSensors(parsedSensors);
  };

  const onCloseTagEditor = () => {
    const parsedSensors = sensors.map((sensor) => ({ ...sensor, isEditingTagList: false }));
    setSensors(parsedSensors);
    setTemporalTagList([]);
  };

  const onChangeTagList = (values: string[] | null) => {
    const parsedTags = parseTags(values);
    setTemporalTagList(parsedTags);
  };

  const onSaveTagList = async (selectedSensor: CustomSensorModel) => {
    setIsSavingTagList(true);
    try {
      const data: UpdateSensorTagListDataModel[] = getTagListSingleSensorData({ sensor: selectedSensor, tags: temporalTagList });
      await UpdateSensorTagList({ host, token, data });
      setMessageSuccess({ description: t('successfully_updated') });
      await getSensorList({ assetId, showAll, page: currentPage, size: currentSize, filters: currentFilterValue });
    } catch (error) {
      setMessageError({ description: t('EDIT_ERROR') });
    } finally {
      setIsSavingTagList(false);
    }
  };

  const onFinishTagEditor = async (values: EditSensorFormValues) => {
    setLoading(true);
    try {
      !!currentSelectedSensor && (await EditSensorById(currentSelectedSensor.id, { ...currentSelectedSensor, name: values.name }));
      setMessageSuccess({ description: t('successfully_updated') });
      setIsEditSensorModalVisible(false);
      setCurrentSelectedSensor(undefined);
      await getSensorList({ assetId, showAll, page: currentPage, size: currentSize, filters: currentFilterValue });
    } catch (error) {
      setMessageError({ description: t('EDIT_ERROR') });
    } finally {
      setLoading(false);
    }
  };

  const onOpenEditSensorModal = (selectedSensor: CustomSensorModel) => {
    setIsEditSensorModalVisible(true);
    setCurrentSelectedSensor(selectedSensor);
  };

  const onFilterTagList = (input: string, option?: DefaultOptionType): boolean => {
    return String(option?.label ?? '')
      .toLowerCase()
      .includes(input.toLowerCase());
  };

  const columns = SensorColumns({
    sensorColumnsLiterals,
    temporalTagList,
    onOpenModal: onOpenEditSensorModal,
    onOpenTagEditor,
    onEditSensor,
    onSaveTagList,
    onCloseTagEditor,
    onChangeTagList,
    onFilterTagList,
    isSavingTagList,
    isUserSuperAdmin,
  });

  const onFinishTagEditorFailed = () => {
    setLoading(false);
  };

  const onCloseEditSensorModal = () => {
    setIsEditSensorModalVisible(false);
    setCurrentSelectedSensor(undefined);
  };

  const deleteSelectedSensor = (newSelectedSensors: CustomSensorModel[]): CustomSensorModel[] => {
    return selectedSensors.filter(({ id, page }) => {
      return (
        page !== currentPage ||
        (page === currentPage && !!newSelectedSensors.find(({ id: selectedSensorId }) => selectedSensorId.includes(id)))
      );
    });
  };

  const getSelectedSensors = (newSelectedSensors: CustomSensorModel[]) => {
    const filteredSelectedSensors = selectedSensors.filter((selectedKey) => selectedKey.page === currentPage);
    const hasNewSelectedSensors = newSelectedSensors.length > filteredSelectedSensors.length;
    const newFilteredSelectedSensors = newSelectedSensors.filter(
      ({ id: selectedSensorId }) => !filteredSelectedSensors.some(({ id }) => id === selectedSensorId)
    );

    const updatedSelectedSensors = hasNewSelectedSensors
      ? [...selectedSensors, ...newFilteredSelectedSensors]
      : deleteSelectedSensor(newSelectedSensors);

    setSelectedSensors(updatedSelectedSensors);
  };

  const setNewSensorList = (selectedSensors: CustomSensorModel[]) => {
    const newSensorList = sensors.map((sensor) => {
      const isRowSelected = !!selectedSensors.find(({ id }) => id.includes(sensor.id));

      return { ...sensor, isRowSelected };
    });
    setSensors(newSensorList);
  };

  const onSelectSensorRow = (newSelectedSensors: CustomSensorModel[]) => {
    setNewSensorList(newSelectedSensors);
    getSelectedSensors(newSelectedSensors);
  };

  const onDeselectAllSensors = () => {
    const deselectedSensors = sensors.map((sensor) => ({ ...sensor, isRowSelected: false, disabledRowSelection: false }));
    setSensors(deselectedSensors);
    setSelectedSensors([]);
  };

  const getMultipleSelectedRows = (currentSelectedSensors: CustomSensorModel[]) => {
    const selectedRows: Key[] = [];
    const selectedTags: string[] = [];
    const filteredSensors = !!currentSelectedSensors.length ? currentSelectedSensors : sensors.filter((sensor) => sensor.tags.length);
    const filteredTags = filteredSensors.flatMap(({ availableTags }) => availableTags.filter((tag) => !selectedTags.includes(tag)));

    const tags = mapTagsToLabels(filteredTags);
    tags.forEach((tag) => {
      filteredSensors
        .filter((sensor) => sensor.tags.includes(tag.value as string))
        .map((sensor) => selectedRows.push(`${tag.value}:${sensor.id}`));
    });
    setMultipleTagEditorRowsSelected(selectedRows);
  };

  const getMultipleTagsList = (currentSelectedSensors: CustomSensorModel[]) => {
    const selectedTags: string[] = [];
    const filteredSensors = !!currentSelectedSensors.length ? currentSelectedSensors : sensors;
    filteredSensors.forEach(({ availableTags }) => {
      const newTags = availableTags.filter((tag) => !selectedTags.includes(tag));
      selectedTags.push(...newTags);
    });
    const tags = mapTagsToLabels(selectedTags);
    const parsedTags: MultipleItemListModel[] = tags.map((tag) => {
      const filteredSelectedSensors = filteredSensors
        .filter((sensor) => sensor.availableTags.includes(tag.value))
        .map((item) => {
          return {
            key: `${tag.value}:${item.id}`,
            label: item.name,
            isParent: false,
            type: item.sensorType,
          };
        });

      return {
        key: tag.value as string,
        label: tag.label as string,
        type: '',
        isParent: true,
        children: filteredSelectedSensors,
      };
    });

    setMultipleTagList(parsedTags);
  };

  const onOpenMultipleTagsEditorModal = () => {
    setMultipleTagEditorModalVisible(true);
    getMultipleTagsList(selectedSensors);
    getMultipleSelectedRows(selectedSensors);
  };

  const onCloseMultipleTagEditorModal = () => {
    setMultipleTagEditorModalVisible(false);
    setMultipleTagEditorRowsSelected([]);
  };

  const updateParamsValues = (newPage: string, newSize: string, newFilters: string) => {
    setCurrentPage(Number(newPage));
    setCurrentSize(Number(newSize));
    setCurrentFilterValue(newFilters);
  };

  const navigateToDefaultValues = (newPage: string, newSize: string, newFilters: string) => {
    const defaultRoute = getAssetWithParamsPathById({ assetId, tab, page: newPage, size: newSize, filters: newFilters });
    history.replace(defaultRoute);
  };

  const onChangePage = (pageNumber: number, pageSize?: number) => {
    history.push(
      getAssetWithParamsPathById({
        assetId,
        tab,
        page: String(pageNumber),
        size: String(pageSize!),
        filters: currentFilterValue,
      })
    );
  };

  const onApplyFilters = (url: string) => {
    setSelectedSensors([]);
    history.push(
      getAssetWithParamsPathById({
        assetId,
        tab,
        page: TABLE_PAGINATION_PAGE_DEFAULT,
        size: String(currentSize),
        filters: url,
      })
    );
  };

  const onChangeMultipleTagEditorSensor = (keys: Key[], selectedRows: MultipleItemListModel[]) => {
    const parsedKeys = getFilteredMultipleTagEditorSelectedRows(selectedRows);
    setMultipleTagEditorRowsSelected(parsedKeys);
  };

  const onUpdateGatewayWithNewSensors = async () => {
    try {
      setUpdating(true);
      const totalSensorsResponse = await GetSensorsListByAssetId({
        host,
        token,
        assetId,
        showAll,
        page: Number(TABLE_PAGINATION_PAGE_DEFAULT),
        size: Number(TABLE_PAGINATION_SIZE_ALL_RESULTS),
        search: '',
        filters: parseFiltersToBeSent('', defaultFilter),
      });
      if (!!totalSensorsResponse.data.length) {
        const filteredSensors = getParsedSensorsToBeUpdated({
          sensors: totalSensorsResponse.data,
          tags: multipleTagEditorRowsSelected,
          currentPageSensors: sensors,
        });
        await UpdateSensorTagList({ token, host, data: filteredSensors });
        setMessageSuccess({ description: t('successfully_updated') });
        setMultipleTagEditorModalVisible(false);
        setMultipleTagEditorRowsSelected([]);
        await getSensorList({ assetId, showAll, page: currentPage, size: currentSize, filters: currentFilterValue });
      }
    } catch (error) {
      setMessageError({ description: t('_CONSUMPTION_SENSORS_LIST_DATA_UPDATING_MULTIPLE_TAGS_ERROR_MESSAGE') });
    } finally {
      setUpdating(false);
    }
  };

  const formOptions: EditSensorFormModel = {
    form,
    onFinish: onFinishTagEditor,
    onFinishFailed: onFinishTagEditorFailed,
    loading,
  };

  const rowSelectionConfig: TableRowSelection<CustomSensorModel> | undefined = {
    type: 'checkbox',
    onChange: (_keys, selectedSensors) => onSelectSensorRow(selectedSensors),
    hideSelectAll: true,
    selectedRowKeys: selectedSensors.map(({ id }) => id),
  };

  const editSensorOptions: EditSensorOptionsModel = {
    sensor: currentSelectedSensor,
    literals: editSensorModalLiterals,
    formOptions,
    visible: isEditSensorModalVisible,
    onOpenEditSensorModal,
    onCloseEditSensorModal,
  };

  const multipleTagEditorModalOptions: MultipleTagEditorOptionsModel = {
    translations: multipleTagEditorLiterals,
    tags: multipleTagList,
    columns: ColumnsMultipleTagEditorModal(multipleTagEditorLiterals.columnsTitle),
    onCloseMultipleTagEditorModal,
    onChangeMultipleTagEditorSensor,
    onUpdateGatewayWithNewSensors,
    multipleTagEditorModalVisible,
    loading: false,
    selectedRowKeys: multipleTagEditorRowsSelected,
    updating,
  };

  const getSensorTypeFilters = () => {
    const item: TableFilterBasicModel = {
      key: sensorTypeKey,
      label: t('_CONSUMPTION_LIST_FILTER_SENSOR_TYPE_LABEL'),
      type: TableFilterType.CHECKBOX,
    };
    const options: TableFilterBasicOptionModel[] = Object.values(SensorType).map((option) => ({
      key: sensorTypeKey,
      value: option,
      label: sensorTypeLiterals[option](),
    }));

    return { item, options };
  };

  const getSensorUsageFilters = () => {
    const item: TableFilterBasicModel = {
      key: sensorUsageKey,
      label: t('_CONSUMPTION_LIST_FILTER_SENSOR_USAGE_LABEL'),
      type: TableFilterType.CHECKBOX,
    };
    const options: TableFilterBasicOptionModel[] = Object.values(SensorUsage).map((option) => ({
      key: sensorUsageKey,
      value: option,
      label: sensorUsageLiterals[option](),
    }));

    return { item, options };
  };

  const getCustomFilters = (currentFilters: string) => {
    const sensorTypeFilter = getSensorTypeFilters();
    const sensorUsageFilter = getSensorUsageFilters();
    const items = [sensorTypeFilter.item, sensorUsageFilter.item];
    const options = [...sensorTypeFilter.options, ...sensorUsageFilter.options];
    const customInitialFilters = getFilters({ items, options, currentFilters });

    setCustomFilters(customInitialFilters);
  };

  useEffect(() => {
    const newPage = page || TABLE_PAGINATION_PAGE_DEFAULT;
    const newSize = size || TABLE_PAGINATION_SIZE_DEFAULT;
    const newFilters = filters || '';

    (!page || !size) && navigateToDefaultValues(newPage, newSize, newFilters);

    if (!!page && !!size) {
      getSensorList({
        assetId,
        page: Number(page),
        size: Number(newSize),
        filters: newFilters,
        search: '',
        showAll,
      });
      updateParamsValues(newPage, newSize, newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, filters]);

  useEffect(() => {
    getCustomFilters(currentFilterValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilterValue]);

  return {
    data: sensors,
    selectedSensor: currentSelectedSensor,
    selectedSensorNumberText,
    deselectAllSensorsButtonText,
    columns,
    sensorColumnsLiterals,
    assignTagsButtonText,
    editSensorOptions,
    maxSizeResponsiveScreen: MAX_SIZE_RESPONSIVE_SCREEN,
    currentPage,
    currentSize,
    totalPages,
    filters: customFilters,
    rowSelectionConfig,
    multipleTagEditorModalOptions,
    getSensorById,
    onSelectSensorAsset,
    onEditSensor,
    onSelectSensorRow,
    onChangePage,
    onApplyFilters,
    onOpenMultipleTagsEditorModal,
    onDeselectAllSensors,
    loading: isLoading,
    isSavingTagList,
    isUserSuperAdmin,
    isDeselectButtonDisabled: !selectedSensors.length,
  };
};
